import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../contexts/UserContext';
import {
  Plus,
  FileText,
  Search,
  Edit2,
  Trash2,
  Eye,
  Download,
  Share2,
  CheckCircle,
  XCircle,
  Calendar,
  MapPin,
  Building,
  Copy,
  Image as ImageIcon
} from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

const JSAList = () => {
  const { user } = useContext(UserContext);
  const [jsaList, setJsaList] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingJSA, setViewingJSA] = useState(null);
  const [editingJSA, setEditingJSA] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [creatorPreviewLoading, setCreatorPreviewLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    // Document Control
    document_id: '',
    owner: '',
    author: user?.first_name + ' ' + user?.last_name || '',
    version: '01',
    // Section 1 - Site Identification
    project_name: '',
    contractor_subcontractor: '',
    site_location: '',
    start_date: new Date().toISOString().split('T')[0],
    finish_date: new Date().toISOString().split('T')[0],
    // Section 2 - Task Preparation
    job_name: '',
    work_instruction_id: '',
    substances: '',
    equipment_tools: '',
    ppe_requirements: '',
    // Section 3 - Job Steps
    job_steps: [{ step: '', hazards: '', control_measures: '', image_url: '' }],
    // Section 4 - Sign Off
    participants: [{ name: '', job_title: '', company: '' }],
    hse_professional_name: '',
    // Hazardous Reference sections
    hazard_sections: [
      {
        title: 'SAFETY HAZARDS',
        entries: [
          { item: 'Slips, trips, falls' },
          { item: 'Falling objects' },
          { item: 'Moving machinery' }
        ]
      },
      {
        title: 'PHYSICAL HAZARDS',
        entries: [
          { item: 'Noise' },
          { item: 'Vibration' },
          { item: 'Extreme temperatures' }
        ]
      },
      {
        title: 'CHEMICAL HAZARDS',
        entries: [
          { item: 'Dust/fumes' },
          { item: 'Flammable materials' },
          { item: 'Toxic substances' }
        ]
      },
      {
        title: 'ERGONOMIC HAZARDS',
        entries: [
          { item: 'Manual handling' },
          { item: 'Repetitive motion' },
          { item: 'Awkward postures' }
        ]
      },
      {
        title: 'BIOLOGICAL HAZARDS',
        entries: [
          { item: 'Bacteria/viruses' },
          { item: 'Insects/animals' },
          { item: 'Mold/fungi' }
        ]
      }
    ],
    // Instructions sections (dynamic)
    instructions_sections: [
      {
        title: 'Section 1.\n"Site Identification"',
        entries: [
          { number: '1', field: 'Project Name' },
          { number: '2', field: 'Contractor/Subcontractor' },
          { number: '3', field: 'Site Location' },
          { number: '4', field: 'Start Date' },
          { number: '5', field: 'Finish Date' }
        ]
      },
      {
        title: 'Section 2.\n"Task Preparation"',
        entries: [
          { number: '6', field: 'Name of the Job' },
          { number: '7', field: 'Work Instruction ID' },
          { number: '8', field: 'Substances' },
          { number: '9', field: 'Equipment and Tools' },
          { number: '10', field: 'Personal Protection Equipment' }
        ]
      },
      {
        title: 'Section 3.\n"Job Description Step by Step"',
        entries: [
          { number: '11', field: 'Basic Job Steps' },
          { number: '12', field: 'Potential Hazards' },
          { number: '13', field: 'Control Measures' }
        ]
      },
      {
        title: 'Section 4.\n"Sign Off"',
        entries: [
          { number: '14', field: 'Name' },
          { number: '15', field: 'Job Title' },
          { number: '16', field: 'Signature' },
          { number: '17', field: 'Date' },
          { number: '18', field: 'Employer/Company' },
          { number: '19', field: 'SM/Site HSE Professional' }
        ]
      }
    ]
  });

  useEffect(() => {
    fetchJSAList();
    fetchTemplates();
    fetchProjects();
  }, []);

  // Auto-refresh preview when modal is open and form data changes
  useEffect(() => {
    if (!showModal) return;

    const timer = setTimeout(() => {
      handleCreatorPreview(formData);
    }, 500); // Debounce 500ms

    return () => clearTimeout(timer);
  }, [showModal, formData]);

  const fetchJSAList = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/jsa/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      setJsaList(response.data);
    } catch (error) {
      console.error('Error fetching JSA list:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/jsa-templates/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/projects/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      const activeProjects = response.data.filter(project => project.is_active !== false);
      setProjects(activeProjects);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleCreate = () => {
    setEditingJSA(null);
    setFormData({
      document_id: '',
      owner: '',
      author: user?.first_name + ' ' + user?.last_name || '',
      version: '01',
      project_name: '',
      contractor_subcontractor: '',
      site_location: '',
      start_date: new Date().toISOString().split('T')[0],
      finish_date: new Date().toISOString().split('T')[0],
      job_name: '',
      work_instruction_id: '',
      substances: '',
      equipment_tools: '',
      ppe_requirements: '',
      job_steps: [{ step: '', hazards: '', control_measures: '', image_url: '' }],
      participants: [{ name: '', job_title: '', company: '' }],
      hse_professional_name: '',
      hazard_sections: [
        { title: 'SAFETY HAZARDS', entries: [{ item: 'Slips, trips, falls' }, { item: 'Falling objects' }, { item: 'Moving machinery' }] },
        { title: 'PHYSICAL HAZARDS', entries: [{ item: 'Noise' }, { item: 'Vibration' }, { item: 'Extreme temperatures' }] },
        { title: 'CHEMICAL HAZARDS', entries: [{ item: 'Dust/fumes' }, { item: 'Flammable materials' }, { item: 'Toxic substances' }] },
        { title: 'ERGONOMIC HAZARDS', entries: [{ item: 'Manual handling' }, { item: 'Repetitive motion' }, { item: 'Awkward postures' }] },
        { title: 'BIOLOGICAL HAZARDS', entries: [{ item: 'Bacteria/viruses' }, { item: 'Insects/animals' }, { item: 'Mold/fungi' }] }
      ],
      instructions_sections: [
        {
          title: 'Section 1.\n"Site Identification"',
          entries: [
            { number: '1', field: 'Project Name' },
            { number: '2', field: 'Contractor/Subcontractor' },
            { number: '3', field: 'Site Location' },
            { number: '4', field: 'Start Date' },
            { number: '5', field: 'Finish Date' }
          ]
        },
        {
          title: 'Section 2.\n"Task Preparation"',
          entries: [
            { number: '6', field: 'Name of the Job' },
            { number: '7', field: 'Work Instruction ID' },
            { number: '8', field: 'Substances' },
            { number: '9', field: 'Equipment and Tools' },
            { number: '10', field: 'Personal Protection Equipment' }
          ]
        },
        {
          title: 'Section 3.\n"Job Description Step by Step"',
          entries: [
            { number: '11', field: 'Basic Job Steps' },
            { number: '12', field: 'Potential Hazards' },
            { number: '13', field: 'Control Measures' }
          ]
        },
        {
          title: 'Section 4.\n"Sign Off"',
          entries: [
            { number: '14', field: 'Name' },
            { number: '15', field: 'Job Title' },
            { number: '16', field: 'Signature' },
            { number: '17', field: 'Date' },
            { number: '18', field: 'Employer/Company' },
            { number: '19', field: 'SM/Site HSE Professional' }
          ]
        }
      ]
    });
    if (previewPdfUrl) {
      URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl('');
    }
    setShowModal(true);
  };

  const closeModal = () => {
    if (previewPdfUrl) {
      URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl('');
    }
    setShowModal(false);
  };

  const handleEdit = (jsa) => {
    setEditingJSA(jsa);
    setFormData({
      ...jsa,
      job_steps: jsa.job_steps?.length ? jsa.job_steps : [{ step: '', hazards: '', control_measures: '', image_url: '' }],
      participants: jsa.participants?.length ? jsa.participants : [{ name: '', job_title: '', company: '' }],
      hse_professional_name: jsa.hse_professional_name || '',
      hazard_sections: jsa.hazard_sections?.length ? jsa.hazard_sections : [
        { title: 'SAFETY HAZARDS', entries: [{ item: 'Slips, trips, falls' }] },
        { title: 'PHYSICAL HAZARDS', entries: [{ item: 'Noise' }] },
        { title: 'CHEMICAL HAZARDS', entries: [{ item: 'Dust/fumes' }] },
        { title: 'ERGONOMIC HAZARDS', entries: [{ item: 'Manual handling' }] },
        { title: 'BIOLOGICAL HAZARDS', entries: [{ item: 'Bacteria/viruses' }] }
      ],
      instructions_sections: jsa.instructions_sections?.length ? jsa.instructions_sections : [
        { title: 'Section 1.\n"Site Identification"', entries: [{ number: '1', field: 'Project Name' }] },
        { title: 'Section 2.\n"Task Preparation"', entries: [{ number: '6', field: 'Name of the Job' }] },
        { title: 'Section 3.\n"Job Description Step by Step"', entries: [{ number: '11', field: 'Basic Job Steps' }] },
        { title: 'Section 4.\n"Sign Off"', entries: [{ number: '14', field: 'Name' }] }
      ]
    });
    if (previewPdfUrl) {
      URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl('');
    }
    setShowModal(true);
  };

  const handleCopy = (jsa) => {
    setEditingJSA(null);
    setFormData({
      ...jsa,
      id: undefined,
      document_id: '',
      start_date: new Date().toISOString().split('T')[0],
      finish_date: new Date().toISOString().split('T')[0],
      is_shared: false,
      participant_signatures: {},
      job_steps: jsa.job_steps?.length ? jsa.job_steps : [{ step: '', hazards: '', control_measures: '', image_url: '' }]
    });
    setShowModal(true);
  };

  const handleView = (jsa) => {
    setViewingJSA(jsa);
    setShowViewModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this JSA?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/jsa/${id}/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      fetchJSAList();
    } catch (error) {
      console.error('Error deleting JSA:', error);
      alert('Failed to delete JSA');
    }
  };

  const handleShare = async (jsa) => {
    if (!window.confirm('Share this JSA for signing?')) return;

    try {
      await axios.post(`${API_BASE_URL}/jsa/${jsa.id}/share/`, {}, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      alert('JSA shared successfully!');
      fetchJSAList();
    } catch (error) {
      console.error('Error sharing JSA:', error);
      alert('Failed to share JSA');
    }
  };

  const handlePreview = async (jsa) => {
    setPreviewLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/jsa/${jsa.id}/pdf/?preview=true`, {
        headers: { Authorization: `Bearer ${user.accessToken}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Error previewing PDF:', error);
      alert('Failed to generate PDF preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async (jsa) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/jsa/${jsa.id}/pdf/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `JSA_${jsa.document_id || jsa.id}_${jsa.job_name?.substring(0, 30) || 'document'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF');
    }
  };

  const closePreview = () => {
    if (previewPdfUrl) {
      URL.revokeObjectURL(previewPdfUrl);
    }
    setPreviewPdfUrl('');
    setShowPreviewModal(false);
  };

  const handleCreatorPreview = async (data = formData) => {
    setCreatorPreviewLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/jsa/preview-pdf/`, data, {
        headers: { Authorization: `Bearer ${user.accessToken}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      if (previewPdfUrl) {
        URL.revokeObjectURL(previewPdfUrl);
      }
      const url = URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
    } catch (error) {
      console.error('Error generating preview:', error);
    } finally {
      setCreatorPreviewLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingJSA) {
        await axios.put(`${API_BASE_URL}/jsa/${editingJSA.id}/`, formData, {
          headers: { Authorization: `Bearer ${user.accessToken}` }
        });
      } else {
        await axios.post(`${API_BASE_URL}/jsa/`, formData, {
          headers: { Authorization: `Bearer ${user.accessToken}` }
        });
      }

      closeModal();
      fetchJSAList();
    } catch (error) {
      console.error('Error saving JSA:', error);
      alert('Failed to save JSA');
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) {
      alert('Please enter a template name');
      return;
    }

    try {
      const templateData = {
        name: templateName,
        owner: formData.owner,
        author: formData.author,
        job_name: formData.job_name,
        work_instruction_id: formData.work_instruction_id,
        substances: formData.substances,
        equipment_tools: formData.equipment_tools,
        ppe_requirements: formData.ppe_requirements,
        job_steps: formData.job_steps
      };

      await axios.post(`${API_BASE_URL}/jsa-templates/`, templateData, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });

      alert('Template saved successfully!');
      setShowTemplateModal(false);
      setTemplateName('');
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template');
    }
  };

  const handleLoadTemplate = (template) => {
    setFormData({
      ...formData,
      owner: template.owner || formData.owner,
      author: template.author || formData.author,
      job_name: template.job_name || '',
      work_instruction_id: template.work_instruction_id || '',
      substances: template.substances || '',
      equipment_tools: template.equipment_tools || '',
      ppe_requirements: template.ppe_requirements || '',
      job_steps: template.job_steps?.length ? template.job_steps : formData.job_steps
    });
  };

  const addJobStep = () => {
    setFormData({
      ...formData,
      job_steps: [...formData.job_steps, { step: '', hazards: '', control_measures: '', image_url: '' }]
    });
  };

  const removeJobStep = (index) => {
    if (formData.job_steps.length === 1) return;
    setFormData({
      ...formData,
      job_steps: formData.job_steps.filter((_, i) => i !== index)
    });
  };

  const updateJobStep = (index, field, value) => {
    const updated = [...formData.job_steps];
    updated[index][field] = value;
    setFormData({ ...formData, job_steps: updated });
  };

  const addParticipant = () => {
    setFormData({
      ...formData,
      participants: [...formData.participants, { name: '', job_title: '', company: '' }]
    });
  };

  const removeParticipant = (index) => {
    if (formData.participants.length === 1) return;
    setFormData({
      ...formData,
      participants: formData.participants.filter((_, i) => i !== index)
    });
  };

  const updateParticipant = (index, field, value) => {
    const updated = [...formData.participants];
    updated[index][field] = value;
    setFormData({ ...formData, participants: updated });
  };

  // Instructions section functions
  const addSection = () => {
    const sections = [...(formData.instructions_sections || [])];
    const newNum = sections.length + 1;
    sections.push({
      title: `Section ${newNum}.\n"New Section"`,
      entries: [{ number: '1', field: '' }]
    });
    setFormData({ ...formData, instructions_sections: sections });
  };

  const removeSection = (sectionIndex) => {
    const sections = formData.instructions_sections || [];
    if (sections.length <= 1) return;
    const filtered = sections.filter((_, i) => i !== sectionIndex);
    setFormData({ ...formData, instructions_sections: filtered });
  };

  const updateSectionTitle = (sectionIndex, title) => {
    const sections = [...(formData.instructions_sections || [])];
    sections[sectionIndex].title = title;
    setFormData({ ...formData, instructions_sections: sections });
  };

  const addEntryToSection = (sectionIndex) => {
    const sections = [...(formData.instructions_sections || [])];
    const entries = sections[sectionIndex].entries || [];
    const lastNum = entries.length > 0 ? parseInt(entries[entries.length - 1].number || '0') : 0;
    sections[sectionIndex].entries.push({ number: (lastNum + 1).toString(), field: '' });
    setFormData({ ...formData, instructions_sections: sections });
  };

  const removeEntryFromSection = (sectionIndex, entryIndex) => {
    const sections = [...(formData.instructions_sections || [])];
    if (sections[sectionIndex].entries.length <= 1) return;
    sections[sectionIndex].entries = sections[sectionIndex].entries.filter((_, i) => i !== entryIndex);
    setFormData({ ...formData, instructions_sections: sections });
  };

  const updateEntry = (sectionIndex, entryIndex, field, value) => {
    const sections = [...(formData.instructions_sections || [])];
    sections[sectionIndex].entries[entryIndex][field] = value;
    setFormData({ ...formData, instructions_sections: sections });
  };

  // Hazard section functions
  const addHazardSection = () => {
    const sections = [...(formData.hazard_sections || [])];
    sections.push({
      title: 'NEW HAZARDS',
      entries: [{ item: '' }]
    });
    setFormData({ ...formData, hazard_sections: sections });
  };

  const removeHazardSection = (sectionIndex) => {
    const sections = formData.hazard_sections || [];
    if (sections.length <= 1) return;
    const filtered = sections.filter((_, i) => i !== sectionIndex);
    setFormData({ ...formData, hazard_sections: filtered });
  };

  const updateHazardSectionTitle = (sectionIndex, title) => {
    const sections = [...(formData.hazard_sections || [])];
    sections[sectionIndex].title = title;
    setFormData({ ...formData, hazard_sections: sections });
  };

  const addHazardEntry = (sectionIndex) => {
    const sections = [...(formData.hazard_sections || [])];
    sections[sectionIndex].entries.push({ item: '' });
    setFormData({ ...formData, hazard_sections: sections });
  };

  const removeHazardEntry = (sectionIndex, entryIndex) => {
    const sections = [...(formData.hazard_sections || [])];
    if (sections[sectionIndex].entries.length <= 1) return;
    sections[sectionIndex].entries = sections[sectionIndex].entries.filter((_, i) => i !== entryIndex);
    setFormData({ ...formData, hazard_sections: sections });
  };

  const updateHazardEntry = (sectionIndex, entryIndex, value) => {
    const sections = [...(formData.hazard_sections || [])];
    sections[sectionIndex].entries[entryIndex].item = value;
    setFormData({ ...formData, hazard_sections: sections });
  };

  const filteredJSA = jsaList.filter(jsa =>
    jsa.job_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    jsa.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    jsa.site_location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6" />
            Job Safety Analysis (JSA)
          </h1>
          <p className="text-gray-600 mt-1">Manage job safety analysis documents</p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          New JSA
        </button>
      </div>

      {/* Search */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by job name, project, location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
        </div>
      </div>

      {/* JSA List */}
      {filteredJSA.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border">
          <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No JSA documents found</h3>
          <p className="text-gray-500 mt-1">Create your first Job Safety Analysis</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Job Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Project</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Location</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Status</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredJSA.map(jsa => (
                <tr key={jsa.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium">{jsa.job_name}</div>
                    <div className="text-sm text-gray-500">{jsa.document_id || `JSA-${jsa.id}`}</div>
                  </td>
                  <td className="px-4 py-3 text-sm">{jsa.project_name}</td>
                  <td className="px-4 py-3 text-sm">{jsa.site_location || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    {jsa.start_date ? new Date(jsa.start_date).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${
                      jsa.is_shared ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {jsa.is_shared ? (
                        <>
                          <CheckCircle className="w-3 h-3" />
                          Shared
                        </>
                      ) : (
                        'Draft'
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handlePreview(jsa)}
                        className="p-1.5 hover:bg-gray-100 rounded"
                        title="Preview PDF"
                        disabled={previewLoading}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDownload(jsa)}
                        className="p-1.5 hover:bg-gray-100 rounded"
                        title="Download PDF"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(jsa)}
                        className="p-1.5 hover:bg-gray-100 rounded"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleCopy(jsa)}
                        className="p-1.5 hover:bg-gray-100 rounded"
                        title="Copy"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      {!jsa.is_shared && (
                        <button
                          onClick={() => handleShare(jsa)}
                          className="p-1.5 hover:bg-blue-100 rounded text-blue-600"
                          title="Share for signing"
                        >
                          <Share2 className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(jsa.id)}
                        className="p-1.5 hover:bg-red-100 rounded text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[95vw] h-[95vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">
                {editingJSA ? 'Edit JSA' : 'Create New JSA'}
              </h3>
              <div className="flex gap-2">
                {templates.length > 0 && (
                  <select
                    onChange={(e) => {
                      const template = templates.find(t => t.id === parseInt(e.target.value));
                      if (template) handleLoadTemplate(template);
                    }}
                    className="px-3 py-1 border rounded text-sm"
                    defaultValue=""
                  >
                    <option value="" disabled>Load Template</option>
                    {templates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                )}
                <button
                  onClick={() => setShowTemplateModal(true)}
                  className="px-3 py-1 border rounded text-sm hover:bg-gray-50"
                >
                  Save as Template
                </button>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 flex gap-4 overflow-hidden">
              {/* Left column - Form */}
              <div className="w-1/2 overflow-y-auto pr-2">

            <form onSubmit={handleSubmit}>
              {/* Document Control */}
              <div className="border rounded-lg p-4 mb-4">
                <h4 className="font-medium mb-3">Document Control</h4>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Document ID</label>
                    <input
                      type="text"
                      value={formData.document_id}
                      onChange={(e) => setFormData({ ...formData, document_id: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="e.g. 0091-0874"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Owner</label>
                    <input
                      type="text"
                      value={formData.owner}
                      onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="e.g. Construction"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Author</label>
                    <input
                      type="text"
                      value={formData.author}
                      onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Version</label>
                    <input
                      type="text"
                      value={formData.version}
                      onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="01"
                    />
                  </div>
                </div>
              </div>

              {/* Section 1 - Site Identification */}
              <div className="border rounded-lg p-4 mb-4">
                <h4 className="font-medium mb-3">Section 1 - Site Identification</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Project Name *</label>
                    <select
                      value={formData.project_name}
                      onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    >
                      <option value="">Select Project</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.name}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Contractor / Subcontractor</label>
                    <input
                      type="text"
                      value={formData.contractor_subcontractor}
                      onChange={(e) => setFormData({ ...formData, contractor_subcontractor: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Site Location</label>
                    <input
                      type="text"
                      value={formData.site_location}
                      onChange={(e) => setFormData({ ...formData, site_location: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium mb-1">Start Date *</label>
                      <input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Finish Date</label>
                      <input
                        type="date"
                        value={formData.finish_date}
                        onChange={(e) => setFormData({ ...formData, finish_date: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 2 - Task Preparation */}
              <div className="border rounded-lg p-4 mb-4">
                <h4 className="font-medium mb-3">Section 2 - Task Preparation</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Name of the Job *</label>
                    <input
                      type="text"
                      value={formData.job_name}
                      onChange={(e) => setFormData({ ...formData, job_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Work Instruction ID</label>
                    <input
                      type="text"
                      value={formData.work_instruction_id}
                      onChange={(e) => setFormData({ ...formData, work_instruction_id: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="e.g. 0084-3388 or N/A"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Substances</label>
                    <textarea
                      value={formData.substances}
                      onChange={(e) => setFormData({ ...formData, substances: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      rows="2"
                      placeholder="e.g. Acids, Resins, Diesel, or N/A"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Equipment and Tools</label>
                    <textarea
                      value={formData.equipment_tools}
                      onChange={(e) => setFormData({ ...formData, equipment_tools: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      rows="2"
                      placeholder="e.g. Crane, forklift, electrical tools"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Personal Protection Equipment (PPE)</label>
                    <textarea
                      value={formData.ppe_requirements}
                      onChange={(e) => setFormData({ ...formData, ppe_requirements: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      rows="2"
                      placeholder="e.g. Safety boots, Safety glasses, Hi-Viz, safety gloves"
                    />
                  </div>
                </div>
              </div>

              {/* Section 3 - Job Steps */}
              <div className="border rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium">Section 3 - Job Description Step by Step</h4>
                  <button
                    type="button"
                    onClick={addJobStep}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                  >
                    + Add Step
                  </button>
                </div>

                {formData.job_steps.map((step, index) => (
                  <div key={index} className="border rounded p-3 mb-3 bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-sm">Step {index + 1}</span>
                      {formData.job_steps.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeJobStep(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1">Basic Job Steps</label>
                        <textarea
                          value={step.step}
                          onChange={(e) => updateJobStep(index, 'step', e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          rows="3"
                          placeholder="Describe the step..."
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Potential Hazards</label>
                        <textarea
                          value={step.hazards}
                          onChange={(e) => updateJobStep(index, 'hazards', e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          rows="3"
                          placeholder="What could go wrong..."
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Control Measures</label>
                        <textarea
                          value={step.control_measures}
                          onChange={(e) => updateJobStep(index, 'control_measures', e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          rows="3"
                          placeholder="How to prevent..."
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Section 4 - Sign Off */}
              <div className="border rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium">Section 4 - Sign Off</h4>
                  <button
                    type="button"
                    onClick={addParticipant}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                  >
                    + Add Participant
                  </button>
                </div>

                {formData.participants?.map((participant, index) => (
                  <div key={index} className="border rounded p-3 mb-3 bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-sm">Participant {index + 1}</span>
                      {formData.participants.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeParticipant(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1">Name</label>
                        <input
                          type="text"
                          value={participant.name}
                          onChange={(e) => updateParticipant(index, 'name', e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          placeholder="Full name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Job Title</label>
                        <input
                          type="text"
                          value={participant.job_title}
                          onChange={(e) => updateParticipant(index, 'job_title', e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          placeholder="Position"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Employer/Company</label>
                        <input
                          type="text"
                          value={participant.company}
                          onChange={(e) => updateParticipant(index, 'company', e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          placeholder="Company name"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <div className="mt-4 pt-4 border-t">
                  <label className="block text-sm font-medium mb-1">SM/Site HSE Professional/Responsable</label>
                  <input
                    type="text"
                    value={formData.hse_professional_name}
                    onChange={(e) => setFormData({ ...formData, hse_professional_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="HSE Professional name"
                  />
                </div>
              </div>

              {/* Instructions */}
              <div className="border rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium">Instructions for Filling out the JSA Form</h4>
                  <button
                    type="button"
                    onClick={addSection}
                    className="text-xs text-green-600 hover:text-green-800 font-medium"
                  >
                    + Add Section
                  </button>
                </div>

                {(formData.instructions_sections || []).map((section, sectionIndex) => (
                  <div key={sectionIndex} className="mb-3">
                    <div className="flex justify-between items-center bg-gray-100 px-2 py-1 rounded">
                      <textarea
                        value={section.title}
                        onChange={(e) => updateSectionTitle(sectionIndex, e.target.value)}
                        className="text-sm font-medium bg-transparent border-none outline-none flex-1 resize-none"
                        rows={2}
                      />
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => addEntryToSection(sectionIndex)}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          + Add
                        </button>
                        <button
                          type="button"
                          onClick={() => removeSection(sectionIndex)}
                          className="text-xs text-red-600 hover:text-red-800 ml-2"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    <table className="w-full text-sm mt-1">
                      <tbody>
                        {(section.entries || []).map((entry, entryIndex) => (
                          <tr key={entryIndex} className="border-b">
                            <td className="px-1 py-1 w-10">
                              <input
                                type="text"
                                value={entry.number}
                                onChange={(e) => updateEntry(sectionIndex, entryIndex, 'number', e.target.value)}
                                className="w-full px-1 py-0.5 border rounded text-xs text-center"
                              />
                            </td>
                            <td className="px-1 py-1">
                              <input
                                type="text"
                                value={entry.field}
                                onChange={(e) => updateEntry(sectionIndex, entryIndex, 'field', e.target.value)}
                                className="w-full px-1 py-0.5 border rounded text-xs"
                              />
                            </td>
                            <td className="px-1 py-1 w-6">
                              <button
                                type="button"
                                onClick={() => removeEntryFromSection(sectionIndex, entryIndex)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>

              {/* Hazardous Reference */}
              <div className="border rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <h4 className="font-medium">Hazardous Reference</h4>
                    <p className="text-xs text-gray-500">Not an all-inclusive list – intend to stimulate ideas for possible risk/hazards</p>
                  </div>
                  <button
                    type="button"
                    onClick={addHazardSection}
                    className="text-xs text-green-600 hover:text-green-800 font-medium"
                  >
                    + Add Category
                  </button>
                </div>

                {/* Safety Icons */}
                <div className="mb-3 p-2 bg-gray-50 rounded">
                  <p className="text-xs text-gray-500 mb-1">Kliknij oznaczenie aby skopiować:</p>
                  <div className="flex flex-wrap gap-1">
                    {[
                      { symbol: '[!]', label: 'Uwaga' },
                      { symbol: '[X]', label: 'Zakaz' },
                      { symbol: '[*]', label: 'Ważne' },
                      { symbol: '(!)', label: 'Ostrzeżenie' },
                      { symbol: '[-]', label: 'Minus' },
                      { symbol: '[+]', label: 'Plus' },
                      { symbol: '[o]', label: 'Punkt' },
                      { symbol: '->', label: 'Strzałka' },
                      { symbol: '>>', label: 'Dalej' },
                      { symbol: '**', label: 'Gwiazdka' },
                      { symbol: '##', label: 'Hash' },
                      { symbol: '@@', label: 'At' }
                    ].map((item, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => navigator.clipboard.writeText(item.symbol)}
                        className="text-sm hover:bg-gray-200 px-2 py-1 rounded cursor-pointer font-mono"
                        title={item.label}
                      >
                        {item.symbol}
                      </button>
                    ))}
                  </div>
                </div>

                {(formData.hazard_sections || []).map((section, sectionIndex) => (
                  <div key={sectionIndex} className="mb-3">
                    <div className="flex justify-between items-center bg-yellow-50 px-2 py-1 rounded">
                      <input
                        type="text"
                        value={section.title}
                        onChange={(e) => updateHazardSectionTitle(sectionIndex, e.target.value)}
                        className="text-sm font-medium bg-transparent border-none outline-none flex-1"
                      />
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => addHazardEntry(sectionIndex)}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          + Add
                        </button>
                        <button
                          type="button"
                          onClick={() => removeHazardSection(sectionIndex)}
                          className="text-xs text-red-600 hover:text-red-800 ml-2"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    <table className="w-full text-sm mt-1">
                      <tbody>
                        {(section.entries || []).map((entry, entryIndex) => (
                          <tr key={entryIndex} className="border-b">
                            <td className="px-1 py-1">
                              <input
                                type="text"
                                value={entry.item}
                                onChange={(e) => updateHazardEntry(sectionIndex, entryIndex, e.target.value)}
                                className="w-full px-1 py-0.5 border rounded text-xs"
                              />
                            </td>
                            <td className="px-1 py-1 w-6">
                              <button
                                type="button"
                                onClick={() => removeHazardEntry(sectionIndex, entryIndex)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-4">
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingJSA ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
              </div>

              {/* Right column - Preview */}
              <div className="w-1/2 flex flex-col border-l pl-4">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-medium">PDF Preview</h4>
                  {creatorPreviewLoading && (
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      Updating...
                    </span>
                  )}
                </div>
                <div className="flex-1 border rounded bg-gray-50 relative">
                  {previewPdfUrl ? (
                    <iframe
                      src={previewPdfUrl}
                      className="w-full h-full"
                      title="JSA PDF Preview"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                        <p>Generating preview...</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewingJSA && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">JSA Details</h3>
              <button onClick={() => setShowViewModal(false)} className="text-gray-500 hover:text-gray-700">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded">
                <div>
                  <p className="text-sm text-gray-500">Document ID</p>
                  <p className="font-medium">{viewingJSA.document_id || `JSA-${viewingJSA.id}`}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Version</p>
                  <p className="font-medium">{viewingJSA.version}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Project</p>
                  <p className="font-medium">{viewingJSA.project_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="font-medium">{viewingJSA.site_location || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Job Name</p>
                  <p className="font-medium">{viewingJSA.job_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Dates</p>
                  <p className="font-medium">
                    {viewingJSA.start_date} - {viewingJSA.finish_date || viewingJSA.start_date}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-2">PPE Requirements</p>
                <p>{viewingJSA.ppe_requirements || 'N/A'}</p>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Job Steps</p>
                <div className="space-y-2">
                  {viewingJSA.job_steps?.map((step, index) => (
                    <div key={index} className="border rounded p-3">
                      <p className="font-medium text-sm mb-1">Step {index + 1}: {step.step}</p>
                      <p className="text-sm text-red-600">Hazards: {step.hazards}</p>
                      <p className="text-sm text-green-600">Controls: {step.control_measures}</p>
                    </div>
                  ))}
                </div>
              </div>

              {Object.keys(viewingJSA.participant_signatures || {}).length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Signatures</p>
                  <div className="border rounded p-3">
                    {Object.entries(viewingJSA.participant_signatures).map(([userId, sig]) => (
                      <div key={userId} className="flex justify-between py-1 border-b last:border-0">
                        <span>{sig.name}</span>
                        <span className="text-sm text-gray-500">{sig.date}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Save as Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium mb-4">Save as Template</h3>
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Template name"
              className="w-full px-3 py-2 border rounded-lg mb-4"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowTemplateModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAsTemplate}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 w-[90vw] h-[90vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">JSA Preview</h3>
              <button
                onClick={closePreview}
                className="text-gray-500 hover:text-gray-700"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1">
              {previewPdfUrl ? (
                <iframe
                  src={previewPdfUrl}
                  className="w-full h-full border rounded"
                  title="JSA PDF Preview"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JSAList;
